// Parseur YAML minimal, scopé aux manifestes Kubernetes.
//
// Pas de dépendance (js-yaml absent, npm install réseau non garanti). Gère le
// sous-ensemble réellement utilisé dans les manifestes CKAD : documents multiples
// (`---`), maps imbriquées, listes (dont listes de maps : containers, ports,
// env), scalaires typés (string/number/bool/null), guillemets, commentaires.

export interface YamlLine {
  indent: number;
  text: string;
}

function tokenize(text: string): YamlLine[] {
  return text
    .split('\n')
    .map((raw) => raw.replace(/\t/g, '  '))
    .map((l) => {
      // Retire un commentaire inline « ... # ... » (approché : # précédé d'espace).
      const ci = l.search(/\s#/);
      const noComment = ci >= 0 ? l.slice(0, ci) : l;
      const indent = noComment.length - noComment.trimStart().length;
      return { indent, text: noComment.trim() };
    })
    .filter((l) => l.text !== '' && !l.text.startsWith('#'));
}

function parseScalar(s: string): unknown {
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
    return s.slice(1, -1);
  }
  // Tableau « flow » inline : [a, b, c] (fréquent en RBAC : verbs: ["get","list"]).
  if (s.startsWith('[') && s.endsWith(']')) {
    const inner = s.slice(1, -1).trim();
    return inner ? inner.split(',').map((x) => parseScalar(x.trim())) : [];
  }
  // Map « flow » vide : {} (fréquent en NetworkPolicy : podSelector: {}).
  if (s === '{}') return {};
  if (s === 'true') return true;
  if (s === 'false') return false;
  if (s === 'null' || s === '~') return null;
  if (/^-?\d+$/.test(s)) return parseInt(s, 10);
  if (/^-?\d+\.\d+$/.test(s)) return parseFloat(s);
  return s;
}

function parseNode(lines: YamlLine[], i: number, indent: number): [unknown, number] {
  if (i >= lines.length || lines[i].indent < indent) return [null, i];

  // Liste : lignes commençant par « - » à cette indentation.
  if (lines[i].text.startsWith('- ') || lines[i].text === '-') {
    const arr: unknown[] = [];
    while (i < lines.length && lines[i].indent === indent && (lines[i].text.startsWith('- ') || lines[i].text === '-')) {
      const rest = lines[i].text === '-' ? '' : lines[i].text.slice(2).trim();
      if (rest === '') {
        // Élément structuré sur les lignes suivantes.
        const childIndent = i + 1 < lines.length ? lines[i + 1].indent : indent + 2;
        const [val, ni] = parseNode(lines, i + 1, childIndent);
        arr.push(val);
        i = ni;
      } else if (/^[\w.-]+:(\s|$)/.test(rest)) {
        // Élément map : « - key: value ». On réécrit la ligne à l'indent du map
        // (indent+2) puis on parse le map à partir d'ici.
        const itemIndent = indent + 2;
        lines[i] = { indent: itemIndent, text: rest };
        const [val, ni] = parseMap(lines, i, itemIndent);
        arr.push(val);
        i = ni;
      } else {
        arr.push(parseScalar(rest));
        i++;
      }
    }
    return [arr, i];
  }

  return parseMap(lines, i, indent);
}

function parseMap(lines: YamlLine[], i: number, indent: number): [Record<string, unknown>, number] {
  const obj: Record<string, unknown> = {};
  while (i < lines.length && lines[i].indent === indent && !(lines[i].text.startsWith('- ') || lines[i].text === '-')) {
    const line = lines[i].text;
    const colon = line.indexOf(':');
    if (colon < 0) { i++; continue; }
    const key = line.slice(0, colon).trim();
    const rest = line.slice(colon + 1).trim();
    if (rest === '') {
      const next = i + 1 < lines.length ? lines[i + 1] : null;
      const nextIsList = !!next && (next.text.startsWith('- ') || next.text === '-');
      if (next && next.indent > indent) {
        // Bloc imbriqué plus indenté (map ou liste).
        const [val, ni] = parseNode(lines, i + 1, next.indent);
        obj[key] = val;
        i = ni;
      } else if (next && next.indent === indent && nextIsList) {
        // Liste au MÊME indent que la clé (YAML courant : containers:\n- name: …).
        const [val, ni] = parseNode(lines, i + 1, indent);
        obj[key] = val;
        i = ni;
      } else {
        obj[key] = null;
        i++;
      }
    } else {
      obj[key] = parseScalar(rest);
      i++;
    }
  }
  return [obj, i];
}

/** Parse un texte YAML en tableau de documents (objets JS). */
export function parseYaml(text: string): Record<string, unknown>[] {
  const docs = text
    .split(/^---\s*$/m)
    .map((d) => d.trim())
    .filter(Boolean);
  const out: Record<string, unknown>[] = [];
  for (const doc of docs) {
    const lines = tokenize(doc);
    if (lines.length === 0) continue;
    const [val] = parseNode(lines, 0, lines[0].indent);
    if (val && typeof val === 'object') out.push(val as Record<string, unknown>);
  }
  return out;
}
